import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeProvider';

interface AppFooterProps {
  isCollapsed: boolean;
}

const LINKS = [
  { key: 'docs', to: '/docs' },
  { key: 'support', to: '/support' },
  { key: 'systemStatus', to: '/system-status' },
  { key: 'feedback', to: '/feedback' },
  // Same document as the login footer's Privacy link — a standalone public
  // page rather than an in-app one, so it stays consistent wherever it's linked from.
  { key: 'privacy', to: '/privacy-policy' },
] as const;

/**
 * Pinned footer bar for authenticated screens. Fixed to the viewport bottom,
 * offset to sit only to the right of the sidebar (tracks its collapsed/expanded
 * width) — distinct from the sidebar's own bottom rail, which both this app and
 * the source mockup keep as a separate element.
 *
 * Hidden below `md` (phones — MobileNav's drawer covers navigation there,
 * and there's no room for this row at all). From `md` up, build info and
 * links wrap onto their own line via flexbox whenever the sidebar-adjusted
 * width is too tight for one row — e.g. tablet widths, or a desktop width
 * with the sidebar expanded — instead of a fixed height that clips or
 * breaks text mid-word. It collapses back to a single line automatically
 * once there's genuinely enough room, so no breakpoint-specific tuning is
 * needed as content or language length changes.
 */
export function AppFooter({ isCollapsed }: AppFooterProps): React.JSX.Element {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const year = new Date().getFullYear();
  const version = import.meta.env.VITE_APP_VERSION;

  return (
    <footer
      className={cn(
        'fixed bottom-0 right-0 z-40 hidden flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-t bg-card px-4 py-2 text-[11.5px] text-muted-foreground transition-all duration-300 md:flex',
        isCollapsed ? 'md:left-16' : 'md:left-64'
      )}
    >
      <div className="flex shrink-0 items-center gap-2">
        <img
          src={
            theme === 'dark' ? '/assets/procimo-pr-mark-white.png' : '/assets/procimo-pr-mark.png'
          }
          alt=""
          aria-hidden="true"
          className="h-[13px] w-[13px] object-contain opacity-80"
        />
        <span className="whitespace-nowrap">{t('layout.footer.buildInfo', { year, version })}</span>
      </div>
      <div className="flex flex-wrap justify-end gap-x-4 gap-y-1.5">
        {LINKS.map(({ key, to }) => (
          <Link
            key={key}
            to={to}
            className="whitespace-nowrap font-medium text-accent-text hover:underline"
          >
            {t(`layout.footer.${key}`)}
          </Link>
        ))}
      </div>
    </footer>
  );
}
